import { convertPathwaysTemplateToMessagesState } from "domains/message-component/utils/convertPathwaysTemplateToMessagesState";
import { omit } from "es-toolkit/compat";

import { composeInitialMessageBody } from "./composeMessage";
import {
    COMPOSE_INITIAL_STATE,
    MESSAGE_EMPTY_STATE,
    SELF_BOOK_MESSAGE_BODY,
} from "./constants";
import {
    type Attachment,
    type ComposeActions,
    ComposeActionsTypes,
    type ComposeStateType,
    type MessageEditor,
} from "./reducer.types";
import { convertMessageSettingsToMessageState } from "./utils/convertMessageSettingsToMessageState";

/** Applies actions to the message editor state */
const messageEditorReducer = (
    state: MessageEditor,
    action: ComposeActions,
    options: Pick<
        ComposeStateType,
        | "maxAttachmentCount"
        | "originalBody"
        | "originalGreeting"
        | "originalSignature"
    >,
): MessageEditor => {
    switch (action.type) {
        case ComposeActionsTypes.UpdateMessageBody:
            return {
                ...state,
                messageBody: action.payload.text,
            };
        case ComposeActionsTypes.UpdateMessageSignature:
            return {
                ...state,
                messageSignature: action.payload.text,
            };
        case ComposeActionsTypes.AddAttachments: {
            // If the max attachment limit is 1, do not count failed attachments
            // towards to total as we will automatically try to replace it.
            const filterErrorAttachments = ({ status }: Attachment) =>
                options.maxAttachmentCount === 1 ? status !== "error" : true;

            if (
                state.attachments.filter(filterErrorAttachments).length >=
                options.maxAttachmentCount
            ) {
                return state;
            }
            return {
                ...state,
                attachments: [
                    ...state.attachments,
                    ...action.payload.attachments,
                ].filter(filterErrorAttachments),
            };
        }
        case ComposeActionsTypes.UpdateAttachment:
            return {
                ...state,
                attachments: state.attachments.map((att) => {
                    if (
                        att.id === action.payload.attachmentId &&
                        att.origin === action.payload.attachmentOrigin
                    ) {
                        return action.payload.attachment;
                    }
                    return att;
                }),
            };
        case ComposeActionsTypes.RemoveAttachment: {
            return {
                ...state,
                attachments: state.attachments.filter(
                    (attachment) =>
                        !(
                            attachment.id === action.payload.attachment.id &&
                            attachment.origin ===
                                action.payload.attachment.origin
                        ),
                ),
            };
        }
        case ComposeActionsTypes.UpdateIsPatientResponseEnabled:
            return {
                ...state,
                isPatientResponseEnabled:
                    action.payload.isPatientResponseEnabled,
            };
        case ComposeActionsTypes.SetIsScheduling:
            return {
                ...state,
                isScheduling: action.payload.isScheduling,
            };

        case ComposeActionsTypes.UpdateSendAt:
            return {
                ...state,
                sendAt: action.payload.sendAt,
            };
        case ComposeActionsTypes.AddSelfBookLink: {
            // We don't override the message body if the user has already started editing
            const hasBodyBeenEdited =
                composeInitialMessageBody({
                    messageBody: options.originalBody,
                    messageGreeting: options.originalGreeting,
                }) !== state.messageBody;

            return {
                ...state,
                selfBookLink: action.payload.selfBookLink,
                messageBody: hasBodyBeenEdited
                    ? state.messageBody
                    : composeInitialMessageBody({
                          messageBody: SELF_BOOK_MESSAGE_BODY,
                          messageGreeting: options.originalGreeting,
                      }),
            };
        }
        case ComposeActionsTypes.RemoveSelfBookLink:
            return {
                ...state,
                selfBookLink: null,
            };
        case ComposeActionsTypes.AddNHSAdviceLink:
            return {
                ...state,
                nhsAdviceLink: action.payload.nhsAdviceLink,
            };
        case ComposeActionsTypes.RemoveNHSAdviceLink:
            return {
                ...state,
                nhsAdviceLink: null,
            };
        default:
            return state;
    }
};

/** Finds the current message editor and applies the action to it */
const handleMessageEditorActions = (
    state: ComposeStateType,
    action: ComposeActions,
) => {
    const { messageEditorId = 0 } = action;

    // Only allow removing a message if there are more than one in state
    if (
        action.type === ComposeActionsTypes.RemoveMessage &&
        state.messages.length > 1
    ) {
        return state.messages.filter((_, index) => index !== messageEditorId);
    }

    // Ensure the message editor id is within the bounds of the messages list
    const validMessageEditorId =
        messageEditorId > state.messages.length - 1 || messageEditorId < 0
            ? 0
            : messageEditorId;

    const messageState = state.messages[validMessageEditorId];

    const updatedMessageState = messageEditorReducer(
        messageState,
        action,
        state,
    );

    return state.messages.map((message, index) =>
        index === validMessageEditorId ? updatedMessageState : message,
    );
};

/**
 * Sets the sendAt value based on the current template when removing a template or switching to a different one
 *
 * If the current template is a Pathway we remove the scheduled send time because it's automatically set from the template
 * Otherwise the scheduled send time is set by the user so we don't clear it.
 */
const setSendAtBasedOnCurrentTemplate = (state: ComposeStateType) =>
    state.template.type === "PathwayTemplate" ? null : state.messages[0].sendAt;

const reducerFunction = (
    state: ComposeStateType,
    action: ComposeActions,
): ComposeStateType => {
    const messages = handleMessageEditorActions(state, action);

    switch (action.type) {
        case ComposeActionsTypes.SetErrors: {
            const messageEditorErrors = action.payload.messageEditorErrors;

            return {
                ...state,
                topLevelErrors:
                    Object.keys(action.payload.topLevelErrors).length > 0
                        ? action.payload.topLevelErrors
                        : undefined,
                messages: messages.map((msg, editorId) => {
                    if (editorId in messageEditorErrors) {
                        return {
                            ...msg,
                            messageEditorErrors: messageEditorErrors[editorId],
                        };
                    }
                    return msg;
                }),
            };
        }

        case ComposeActionsTypes.SetShowSelfBookConfiguration:
            return {
                ...state,
                messages,
                isSelfbookConfigurationOpen: action.payload.show,
            };
        case ComposeActionsTypes.UpdateIsSaveToRecordEnabled:
            return {
                ...state,
                messages,
                isSaveToRecordEnabled: action.payload.isSaveToRecordEnabled,
            };
        case ComposeActionsTypes.RemoveTemplate:
            return {
                ...state,
                template: { type: "NoTemplate" },
                messages: [
                    {
                        ...convertMessageSettingsToMessageState({
                            greeting: state.originalGreeting,
                            editableBody: state.originalBody,
                            editableSignature: state.originalSignature,
                        }),
                        // Do not clear sendAt settings when removing a template unless it's a pathway template
                        sendAt: setSendAtBasedOnCurrentTemplate(state),
                    },
                ],
                isSaveToRecordEnabled:
                    COMPOSE_INITIAL_STATE.isSaveToRecordEnabled,
            };
        case ComposeActionsTypes.AddPendingQuestionnaireTemplate: {
            return {
                ...state,
                messages,
                template: {
                    pendingValue: action.payload.pendingTemplate,
                    type: "PendingQuestionnaireTemplate",
                },
            };
        }
        case ComposeActionsTypes.AddQuestionnaireTemplate: {
            return {
                ...state,
                template: {
                    value: action.payload.template,
                    type: "QuestionnaireTemplate",
                    warning: action.payload.warning,
                },
                messages: [
                    {
                        ...convertMessageSettingsToMessageState({
                            greeting: state.originalGreeting,
                            editableBody: action.payload.template.body,
                            editableSignature: state.originalSignature,
                        }),
                        sendAt: setSendAtBasedOnCurrentTemplate(state),
                    },
                ],
            };
        }
        case ComposeActionsTypes.AddMessageTemplate: {
            const templateAttachments: Attachment[] = (
                action.payload.template.attachments || []
            )
                .slice(0, state.maxAttachmentCount)
                .map((att) => {
                    return {
                        origin: "Template",
                        name: att.name ?? "File name missing",
                        id: att.id,
                        previewUrl: att.previewUrl,
                        source: "PatientMessaging",
                        size: att.size,
                        status: "success",
                    };
                });

            const templatedMessage = {
                ...convertMessageSettingsToMessageState({
                    greeting: state.originalGreeting,
                    editableBody: action.payload.template.body,
                    editableSignature: state.originalSignature,
                }),
                isPatientResponseEnabled:
                    action.payload.template.allowReplyByDefault ||
                    MESSAGE_EMPTY_STATE.isPatientResponseEnabled,
                // Replace existing attachments with template attachments
                attachments: templateAttachments,
                // Maintain the sendAt settings when applying a template
                sendAt: setSendAtBasedOnCurrentTemplate(state),
            };

            return {
                ...state,
                messages: [templatedMessage],
                template: {
                    value: omit(action.payload.template, ["attachments"]),
                    type: "MessageTemplate",
                },
            };
        }
        case ComposeActionsTypes.AddPathwayTemplate: {
            return {
                ...state,
                messages: convertPathwaysTemplateToMessagesState(
                    action.payload.template,
                    state,
                ),
                template: {
                    type: "PathwayTemplate",
                    value: action.payload.template,
                },
            };
        }
        case ComposeActionsTypes.UpdateContactDetails:
            return {
                ...state,
                messages,
                contactDetails: action.payload.contactDetails,
            };

        default:
            return {
                ...state,
                messages,
            };
    }
};

export const composeReducer = (
    state: ComposeStateType,
    action: ComposeActions,
): ComposeStateType => {
    return reducerFunction(
        // Clear all errors from state before executing main reducer function
        {
            ...state,
            topLevelErrors: undefined,
            messages: state.messages.map((msg) => ({
                ...msg,
                messageEditorErrors: undefined,
            })),
        },
        action,
    );
};
